import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Select, MenuItem, FormControl, InputLabel, Button, TextField, CircularProgress, Alert
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const API = process.env.REACT_APP_API_URL || "";

function exportCSV(rows, columns, labels, filename) {
  const header = labels.join(",");
  const csv = [header, ...rows.map(r => columns.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function exportPDF(rows, columns, labels, title) {
  const { jsPDF } = await import("jspdf");
  await import("jspdf-autotable");
  const doc = new jsPDF({ orientation: "landscape" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const now = new Date();
  doc.setFontSize(14);
  doc.text(`Avance Asistencia — ${title}`, pageWidth / 2, 16, { align: "center" });
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(`Generado: ${now.toLocaleString("es-MX")}`, pageWidth / 2, 23, { align: "center" });
  doc.setTextColor(0);
  doc.autoTable({
    startY: 28,
    head: [labels],
    body: rows.map(r => columns.map(c => r[c] ?? "")),
    styles: { fontSize: 7, cellPadding: 2, halign: "center" },
    headStyles: { fillColor: [100, 100, 100], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    tableWidth: "wrap",
    margin: { left: 5, right: 5 },
  });
  doc.save(`avance_asistencia_${title.toLowerCase().replace(/ /g, "_")}.pdf`);
}

const pct = (v) => v != null ? `${v}%` : "0%";
const num = (v) => v != null ? Number(v).toLocaleString() : "0";

// Colores estilo guinda
const bgA = "#F9E5E5";
const bgB = "#EDBBBB";

function ReportesAsistencia({ token }) {
  const [tab, setTab] = useState(0);
  const [distritoLocal, setDistritoLocal] = useState("");
  const [usuarioFiltro, setUsuarioFiltro] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState("");
  const [refreshInterval, setRefreshInterval] = useState(300);
  const [refreshActive, setRefreshActive] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let url;
      if (tab === 0) url = `${API}/api/asistencia/por-seccion${distritoLocal ? `?distrito_local=${distritoLocal}` : ""}`;
      else if (tab === 1) url = `${API}/api/asistencia/por-distrito`;
      else if (tab === 2) url = `${API}/api/asistencia/por-entidad`;
      else url = `${API}/api/asistencia/por-usuario?${new URLSearchParams({...(distritoLocal && {distrito_local: distritoLocal}), ...(usuarioFiltro && {usuario_filtro: usuarioFiltro})})}`;
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData(tab === 2 ? (json.data ? [json.data] : []) : (json.data || []));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [tab, distritoLocal, usuarioFiltro]);// eslint-disable-line

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!refreshActive || !refreshInterval) return;
    const id = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(id);
  }, [refreshActive, refreshInterval, fetchData]);

  const getColsLabels = () => {
    if (tab === 0) return {
      cols: ["distrito_local","seccion","total_convencidos","asist_convencidos","pct_convencidos","total_afiliados","asist_afiliados","pct_afiliados","promocion_total","total_asistencia","pct_total"],
      labels: ["Distrito Local","Sección","Total Convencidos","Asistencia","% Asistencia","Afiliados (no conv)","Asistencia Afiliados","% Asistencia","Promoción Total","Total Asistencia","% Asistencia Total"]
    };
    if (tab === 1) return {
      cols: ["distrito_local","total_convencidos","asist_convencidos","pct_convencidos","total_afiliados","asist_afiliados","pct_afiliados","promocion_total","total_asistencia","pct_total"],
      labels: ["Distrito Local","Total Convencidos","Asistencia","% Asistencia","Afiliados (no conv)","Asistencia Afiliados","% Asistencia","Promoción Total","Total Asistencia","% Asistencia Total"]
    };
    if (tab === 2) return {
      cols: ["total_convencidos","asist_convencidos","pct_convencidos","total_afiliados","asist_afiliados","pct_afiliados","promocion_total","total_asistencia","pct_total"],
      labels: ["Total Convencidos","Asistencia","% Asistencia","Afiliados (no conv)","Asistencia Afiliados","% Asistencia","Promoción Total","Total Asistencia","% Asistencia Total"]
    };
    return {
      cols: ["nombre_completo","rol","distrito_local","cantidad_pases_lista"],
      labels: ["Usuario","Rol","Distrito Local","Cantidad Pases de Lista"]
    };
  };

  const { cols, labels } = getColsLabels();
  const tabNames = ["Por Sección", "Por Distrito", "Por Entidad", "Por Usuario"];

  // Totales para tabs 0 y 1
  const showTotales = tab === 0 || tab === 1;
  const totales = showTotales ? data.reduce((acc, r) => {
    acc.total_convencidos += Number(r.total_convencidos) || 0;
    acc.asist_convencidos += Number(r.asist_convencidos) || 0;
    acc.total_afiliados += Number(r.total_afiliados) || 0;
    acc.asist_afiliados += Number(r.asist_afiliados) || 0;
    acc.promocion_total += Number(r.promocion_total) || 0;
    acc.total_asistencia += Number(r.total_asistencia) || 0;
    return acc;
  }, { total_convencidos: 0, asist_convencidos: 0, total_afiliados: 0, asist_afiliados: 0, promocion_total: 0, total_asistencia: 0 }) : null;

  const pctCalc = (a, b) => b ? (a * 100 / b).toFixed(1) + "%" : "0%";

  const isPctCol = (c) => c.startsWith("pct_");
  const cellVal = (r, c) => isPctCol(c) ? pct(r[c]) : (c === "nombre_completo" || c === "rol" ? (r[c] || "—") : num(r[c]));

  // Alternating column group backgrounds
  const cellBg = (ci) => {
    if (tab === 3) return ci % 2 === 0 ? bgA : bgB;
    // Groups: first 1-2 cols = bgA, next 3 = bgB, next 3 = bgA, next 3 = bgB
    if (tab === 0) {
      if (ci < 2) return bgA;
      if (ci < 5) return bgB;
      if (ci < 8) return bgA;
      return bgB;
    }
    if (ci < 1) return bgA;
    if (ci < 4) return bgB;
    if (ci < 7) return bgA;
    return bgB;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", flex: "1 1 100%", fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
          📋 Avance Asistencia
        </Typography>
        {(tab === 0 || tab === 3) && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Distrito Local</InputLabel>
            <Select value={distritoLocal} label="Distrito Local" onChange={e => setDistritoLocal(e.target.value)}>
              <MenuItem value="">Todos</MenuItem>
              {[...Array(16)].map((_, i) => <MenuItem key={i+1} value={i+1}>{i+1}</MenuItem>)}
            </Select>
          </FormControl>
        )}
        {tab === 3 && (
          <TextField size="small" label="Buscar usuario" value={usuarioFiltro}
            onChange={e => setUsuarioFiltro(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") fetchData(); }}
            sx={{ minWidth: 150 }}
          />
        )}
        <Button size="small" variant="outlined" onClick={fetchData} startIcon={<RefreshIcon />}>Refresh</Button>
        <TextField size="small" label="Min" type="number" value={Math.floor(refreshInterval / 60)}
          onChange={e => setRefreshInterval(Math.max(0, Number(e.target.value)) * 60 + (refreshInterval % 60))}
          sx={{ width: 60 }} inputProps={{ min: 0 }}
        />
        <TextField size="small" label="Seg" type="number" value={refreshInterval % 60}
          onChange={e => setRefreshInterval(Math.floor(refreshInterval / 60) * 60 + Math.max(0, Math.min(59, Number(e.target.value))))}
          sx={{ width: 60 }} inputProps={{ min: 0, max: 59 }}
        />
        <Button size="small" variant={refreshActive ? "contained" : "outlined"} color={refreshActive ? "success" : "inherit"}
          onClick={() => setRefreshActive(!refreshActive)} sx={{ minWidth: 70, fontSize: "0.75rem" }}>
          {refreshActive ? "Auto ⏵" : "Pausa ⏸"}
        </Button>
        {data.length > 0 && <>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
            onClick={() => exportCSV(data, cols, labels, `Asistencia_${tabNames[tab].replace(/ /g,"_")}.csv`)}>CSV</Button>
          <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />}
            onClick={() => exportPDF(data, cols, labels, tabNames[tab])}>PDF</Button>
        </>}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 2 }}>
        {tabNames.map((t, i) => <Tab key={i} label={t} />)}
      </Tabs>

      {loading && <CircularProgress size={24} sx={{ display: "block", mx: "auto", my: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && data.length > 0 && (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
          <TableContainer sx={{ maxHeight: "calc(100vh - 300px)", overflowX: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {labels.map((l, i) => (
                    <TableCell key={i} align="center" sx={{ backgroundColor: cellBg(i), fontWeight: "bold", whiteSpace: "nowrap" }}>
                      {l}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((r, i) => (
                  <TableRow key={i}>
                    {cols.map((c, ci) => (
                      <TableCell key={ci} align="center" sx={{ backgroundColor: cellBg(ci) }}>
                        {cellVal(r, c)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {showTotales && (
                  <TableRow sx={{ "& td": { fontWeight: "bold" } }}>
                    {cols.map((c, ci) => {
                      if (c === "distrito_local" || c === "seccion") return <TableCell key={ci} align="center" sx={{ backgroundColor: cellBg(ci) }}>{c === "distrito_local" ? "TOTAL" : ""}</TableCell>;
                      if (isPctCol(c)) {
                        const v = c === "pct_convencidos" ? pctCalc(totales.asist_convencidos, totales.total_convencidos)
                          : c === "pct_afiliados" ? pctCalc(totales.asist_afiliados, totales.total_afiliados)
                          : pctCalc(totales.total_asistencia, totales.promocion_total);
                        return <TableCell key={ci} align="center" sx={{ backgroundColor: cellBg(ci) }}>{v}</TableCell>;
                      }
                      return <TableCell key={ci} align="center" sx={{ backgroundColor: cellBg(ci) }}>{num(totales[c])}</TableCell>;
                    })}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {!loading && data.length === 0 && !error && <Alert severity="info">Sin datos.</Alert>}
    </Box>
  );
}

export default ReportesAsistencia;
